import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { CommonSelect, type Option, type SelectProps } from 'components/common';
import { useInfiniteCreativeTexts } from '../api';
import type { CreativeText, FlowDetails } from '../types';
import { useInView } from 'react-intersection-observer';
import { useFormContext } from 'react-hook-form';
import type { SelectChangeEvent } from '@mui/material';
import type { ListResponse } from 'types/shared';
import { trackCabinetQueryError } from 'lib/datadog';

interface SelectCreativeTextsAttrs {
  open: boolean;
  multiples: boolean;
  placeholder: string;
  asAutocomplete?: boolean;
  name:
    | 'defaultCreativeTextId'
    | 'defaultCreativeTextIds'
    | 'additionalCreativeTextsIds';
  flow: FlowDetails;
  onInputChange?: (value: string) => void;
}

type SelectCreativeTextsProps = Partial<
  Pick<SelectProps, 'handleChange' | 'inputLabel' | 'onClose' | 'required'> &
  SelectCreativeTextsAttrs
>;

const SelectCreativeTexts: FC<SelectCreativeTextsProps> = (
  {
    handleChange,
    name,
    inputLabel,
    multiples,
    placeholder,
    onClose,
    open,
    asAutocomplete,
    required,
    flow,
    onInputChange,
  }) => {
  const { ref, inView } = useInView({
    threshold: 0,
  });
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError, error } =
    useInfiniteCreativeTexts();
  const { watch, setValue, resetField } = useFormContext();

  const defaultCreativeTextId = watch('defaultCreativeTextId') as number;
  const additionalCreativeTextsIds = watch(
    'additionalCreativeTextsIds',
  ) as number[];

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (!isError || !error) {
      return;
    }

    trackCabinetQueryError(error, 'tiktok', 'creative_text');
  }, [isError, error]);

  const options = useMemo(() => {
    if (!data) return [];

    const r = data.pages.flatMap((page: ListResponse<CreativeText>) =>
      page.data
        .map(
          ({ text, id }: CreativeText): Option => ({
            label: text,
            value: Number(id),
          }),
        ),
    );

    // Add default creative text of the flow to the top of the list if it's not already present
    if (
      flow?.default_text &&
      !r.find((option) => option.value === Number(flow?.default_text?.id))
    ) {
      r.unshift({
        label: flow.default_text.text,
        value: Number(flow.default_text.id),
      });
    }

    return r;
  }, [data, flow]);

  const handleSelectChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value;
    if (!value) {
      resetField(name ?? 'creativeText', { defaultValue: null });
      handleChange?.(event);
      return;
    }

    if (
      name === 'defaultCreativeTextId' &&
      typeof value === 'number' &&
      additionalCreativeTextsIds?.includes(value)
    ) {
      setValue(
        'additionalCreativeTextsIds',
        additionalCreativeTextsIds.filter((id) => id !== value),
      );
    }
    if (
      name === 'additionalCreativeTextsIds' &&
      Array.isArray(value) &&
      value.includes(defaultCreativeTextId)
    ) {
      setValue('defaultCreativeTextId', 0);
    }
    handleChange?.(event);
  };

  return (
    <CommonSelect
      name={name ?? 'creativeText'}
      inputLabel={inputLabel ?? 'Creative text'}
      options={options}
      handleChange={handleSelectChange}
      placeholder={placeholder}
      multiples={multiples}
      onClose={onClose}
      open={open}
      loading={isFetchingNextPage}
      loadMoreRef={ref}
      asAutocomplete={asAutocomplete}
      required={required}
      onInputChange={onInputChange}
    />
  );
};

export default SelectCreativeTexts;
